import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createPortal } from "react-dom";
import { Star, Phone, ArrowRight, ChevronDown, X, ChevronLeft, ChevronRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SpecialtyWork } from "@/components/SpecialtyWork";

import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { Button } from "@/components/ui/button";
import { browardCities, caCities, company } from "@/data/site";
import { HeroBackground } from "@/components/HeroBackground";
import { HomeIntro } from "@/components/HomeIntro";
import { CredentialsSection } from "@/components/CredentialsSection";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { SectionDecor } from "@/components/SectionDecor";
import { roofingTiles, solarTiles } from "@/data/serviceTiles";
import heroImage from "@/assets/heroes/home.jpg.asset.json";
import heroPoster from "@/assets/heroes/home-poster.jpg.asset.json";
import heroVideo from "@/assets/mckayhero.mp4.asset.json";
import teamImg from "@/assets/mckay/team-1981.webp.asset.json";
import roofRepairImg from "@/assets/mckay/roof-repair.webp.asset.json";
import roofReplacementImg from "@/assets/mckay/roof-replacement.webp.asset.json";
import solarInstallImg from "@/assets/mckay/solar-install.webp.asset.json";
import roofingServiceImg from "@/assets/mckay/roofing-service.webp.asset.json";
import solarEnergyImg from "@/assets/mckay/solar-energy.webp.asset.json";
import gallery1 from "@/assets/gallery-new/6940e3a8ab25949211304117-2.webp.asset.json";
import gallery2 from "@/assets/gallery-new/6940e3a8b4f420921f7ca893-2.webp.asset.json";
import gallery3 from "@/assets/gallery-new/6940e3a997c0231b045981a5-2.webp.asset.json";
import gallery4 from "@/assets/gallery-new/6940e3a8b4f42026887ca890-2.webp.asset.json";
import gallery5 from "@/assets/gallery-new/6940e3a896e7535d5af6346b-2.webp.asset.json";
import gallery6 from "@/assets/gallery-new/6940e3a896e753e8e1f6346a-2.webp.asset.json";
import gallery7 from "@/assets/gallery-new/6940e3a997c0238f425981a9-2.webp.asset.json";
import gallery8 from "@/assets/gallery-new/6940e3a8ab25944693304115-2.webp.asset.json";
import gallery9 from "@/assets/gallery-new/6940e3a8ca729802452657d2-2.webp.asset.json";

const homeReviews: { n: string; c: string; q: string }[] = [
  { n: "Carlos H.", c: "Escondido, CA", q: "Very happy with McKay, with the work the guys did on my roof in Escondido. Everyone was very professional, especially Josh, who was very attentive to all my questions." },
  { n: "Ted C.", c: "Southern California, CA", q: "I've had a new roof for my apartments, solar for my home and business by McKay Roofing and Solar. They are professional, timely and very capable. I recommend them for your roofing and solar needs." },
  { n: "Enrique V.", c: "Southern California, CA", q: "McKay Roofing is a great company, they are very professional and great customer service from Erick and Josh, we are very satisfied with the entire process!" },
  { n: "Marisol R.", c: "Coronado, CA", q: "They came out the same week I called about a leak over my kitchen. Found the real problem, fixed it, and didn't try to sell me a whole new roof. That honesty is why I'll call them again." },
  { n: "Dan W.", c: "Winter Gardens, CA", q: "Tile roof replacement plus a solar install, all handled by one crew. They walked me through the sequencing so panels went on last. Site was swept clean every single afternoon." },
  { n: "Priya N.", c: "Pine Valley, CA", q: "The permit side of things worried me the most and they took care of all of it. Got updates by text without me having to chase anyone down for answers." },
  { n: "Greg S.", c: "Poway, CA", q: "Bids from three companies and McKay was the only one who got up on the roof and showed me photos of what was actually going on. No pressure, fair price, finished a day early." },
  { n: "Alicia M.", c: "Carlsbad, CA", q: "Our system has been running two summers now with a noticeable drop in the utility bill. When we had a question after the install, someone actually picked up the phone." },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "McKay Roofing & Solar – Southern California Roofing & Solar Contractor" },
      { name: "description", content: "Family-owned roofing and solar since 1981. Founded in Southern California, serving all of Southern California with roof repairs, replacements and solar installs. Free estimates." },
      { name: "keywords", content: "roofing Southern California, roof repair Southern California, roof replacement Carlsbad, solar installation Southern California, solar panels California, residential roofing, commercial roofing, Tesla Powerwall installer, McKay Roofing & Solar" },
      { property: "og:title", content: "McKay Roofing & Solar – Southern California Roofing & Solar Contractor" },
      { property: "og:description", content: "Family-owned roofing and solar since 1981, founded in Southern California and serving all of Southern California." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RoofingContractor",
          name: "McKay Roofing & Solar",
          telephone: company.phone,
          email: company.email,
          address: {
            "@type": "PostalAddress",
            streetAddress: "9520 Padgett St, Suite 107",
            addressLocality: "San Diego",
            addressRegion: "CA",
            postalCode: "92126",
            addressCountry: "US",
          },
          areaServed: [{ "@type": "AdministrativeArea", name: "Southern California, CA" }],
          aggregateRating: { "@type": "AggregateRating", ratingValue: company.rating, reviewCount: company.reviewCount },
        }),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <div className="flex min-h-screen flex-col">
      <HomeIntro />
      <SiteHeader />
      <main className="flex-1">
        {/* Hero – full screen */}
        <section className="relative h-screen min-h-screen w-full overflow-hidden text-primary-foreground">
          <HeroBackground
            src={heroImage.url}
            video={heroVideo.url}
            poster={heroPoster.url}
            alt="Aerial footage of McKay Roofing & Solar crews replacing a roof and installing solar in Southern California"
            overlay="black-soft"
          />
          <div className="relative flex h-full items-center">
            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl">
                <h1 className="text-4xl font-bold leading-[1.1] sm:text-5xl lg:text-6xl text-balance">
                  Southern California&apos;s Trusted Roofing &amp; Solar Contractor since 1981
                </h1>
                <p className="mt-6 max-w-2xl text-base text-primary-foreground/85 sm:text-lg">
                  Free, no-pressure estimates. 150+ five-star Google reviews. 3,200+ homes protected across Southern California.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg" className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                    <Link to="/contact">Get Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/contact">
                      <Phone className="mr-2 h-4 w-4" />
                      {company.phone}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative overflow-hidden border-b border-border bg-gradient-hero text-primary-foreground">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, oklch(0.72 0.13 200 / 0.5), transparent 40%), radial-gradient(circle at 80% 70%, oklch(0.72 0.18 55 / 0.4), transparent 45%)" }} />
          <StatsSection />
        </section>

        {/* Progressive scroll-based story sections */}
        <StorySections />

        {/* Roofing & Solar services grid */}
        <ServicesGrid />

        <SpecialtyWork />

        <RecentJobPhotos />

        {/* Testimonials */}
        <section className="relative overflow-hidden bg-muted/40">
          <SectionDecor variant="sun" side="left" tone="primary" opacity={0.05} size={640} />
          <div className="relative z-10 mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-primary text-balance">
              Reviews From <span className="text-secondary">Southern California</span> Homeowners
            </h2>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-muted-foreground">
              <span>Real feedback from homeowners across Southern California.</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                <Star className="h-3.5 w-3.5 fill-current" /> 150+ reviews
              </span>
            </div>
          </div>
          </div>
          <div className="mck-reviews-marquee mt-10">
            <div className="mck-reviews-track">
              {[0, 1].map((group) => (
                <div key={group} className="mck-reviews-group" aria-hidden={group === 1 || undefined}>
                  {homeReviews.map((t) => (
                    <div key={`${group}-${t.n}`} className="mck-review-slide">
                      <div className="flex w-full flex-col rounded-2xl border border-border bg-card p-6 shadow-sm">
                        <div className="flex text-accent">{[1,2,3,4,5].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
                        <p className="mt-3 text-sm text-foreground/90">"{t.q}"</p>
                        <div className="mt-4 text-xs font-semibold text-primary">{t.n} <span className="font-normal text-muted-foreground">– {t.c}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Credentials, certifications & manufacturer systems */}
        <CredentialsSection className="bg-background" showFeaturedCerts />


        {/* Common Questions */}
        <CommonQuestions />

        {/* Areas */}
        <section className="relative overflow-hidden">
          <SectionDecor variant="house" side="right" tone="primary" opacity={0.05} size={720} />
          <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="text-sm font-bold uppercase tracking-wider text-secondary">Where we work</div>
            <h2 className="mt-2 text-3xl font-bold text-primary text-balance">Every city we serve in Southern California.</h2>
            <p className="mt-4 text-muted-foreground">Based in Carlsbad, our crews cover the full Southern California region every week.</p>
          </div>
          <div className="mx-auto mt-10 grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {caCities.map((c) => (
              <Link
                key={c.slug}
                to="/areas/california/$city"
                params={{ city: c.slug }}
                className="group flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-primary shadow-sm transition-all hover:-translate-y-0.5 hover:border-secondary hover:text-secondary hover:shadow-md"
              >
                <span>{c.name}</span>
                <ArrowRight className="h-4 w-4 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
              </Link>
            ))}
            <Link
              to="/areas-served"
              className="group flex items-center justify-between rounded-xl border border-primary bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-md"
            >
              <span>View Areas</span>
              <ArrowRight className="h-4 w-4 transition-all group-hover:translate-x-0.5" />
            </Link>
          </div>
          </div>
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}

type Story = {
  eyebrow: string;
  title: string;
  paragraphs: string[];
  img: string;
  alt: string;
  href: string;
};

const stories: Story[] = [
  {
    eyebrow: "Since 1981",
    title: "Why choose McKay Roofing and Solar in Southern California",
    paragraphs: [
      "We've worked on roofs all over Southern California for decades. Homes in Mission Valley deal with runoff issues. East County roofs take a beating from heat. Coastal wind lifts shingles faster than people expect. Every area has its own problems, and that's something you only learn by being here a long time.",
      "We handle roofing and solar under one roof, which matters more than people realize. Solar systems sit on roofs, and if those two trades don't work together, problems show up later. Our approach is simple. Inspect first. Explain clearly. Fix what actually needs fixing.",
    ],
    img: teamImg.url,
    alt: "McKay Roofing & Solar team photo from 1981",
    href: "/why-choose-us",
  },
  {
    eyebrow: "Roof Repair",
    title: "Roof repair Southern California",
    paragraphs: [
      "Most roof repair calls start small. A stain that wasn't there before. A drip during the last storm. A shingle that came loose after a windy day. With constant sun and sudden rain, roof problems show up fast in Southern California.",
      "Homeowners reach out when they want answers not guesswork. After years of repairing roofs across Southern California neighborhoods, we know where damage usually starts and how to stop it before it spreads into bigger issues.",
    ],
    img: roofRepairImg.url,
    alt: "McKay technician repairing a shingle roof in Southern California",
    href: "/services/roof-repair",
  },
  {
    eyebrow: "Roof Replacement",
    title: "Roof replacement Southern California",
    paragraphs: [
      "There's a point where fixing the same roof over and over just doesn't make sense anymore. Roof replacement usually comes up when leaks keep popping up, materials are worn thin, or the roof is simply at the end of its life. In Southern California, nonstop sun alone can break roofs down faster than most people expect.",
      "We handle roof replacement for homes and buildings that need a clean reset. Old roofing comes off, the structure underneath gets checked, and a new roof goes on that's built for Southern California heat and weather. We walk through material choices, timing, and what to expect so nothing catches you off guard halfway through.",
    ],
    img: roofReplacementImg.url,
    alt: "Southern California home mid roof replacement with fresh underlayment",
    href: "/services/roof-replacement",
  },
  {
    eyebrow: "Solar Installation",
    title: "Solar Installation Services in Southern California",
    paragraphs: [
      "Solar installation is more than placing panels on a roof. The roof underneath needs to be solid and ready for the system long term.",
      "We install solar systems with roofing in mind from the start. That keeps projects moving smoothly and helps prevent leaks or roof damage later. Many customers choose us because they don't want roofing and solar handled by separate companies.",
    ],
    img: solarInstallImg.url,
    alt: "Solar panels installed on a Southern California residential roof",
    href: "/services/solar-installation",
  },
  {
    eyebrow: "Roofing Service",
    title: "Roofing Service Southern California",
    paragraphs: [
      "Roofing service is the day-to-day work that keeps a roof from turning into a bigger problem later. Inspections, routine repairs, maintenance, and full replacements when a roof has finally run its course.",
      "In Southern California, roofs don't get much of a break. Sun dries materials out, wind shifts things loose and rain finds the smallest gaps. Our roofing service focuses on spotting issues early and laying out clear options, not rushing people into work that doesn't make sense yet.",
    ],
    img: roofingServiceImg.url,
    alt: "Wood shake roof being serviced in Southern California",
    href: "/services/roofing",
  },
  {
    eyebrow: "Solar Energy Company",
    title: "Solar Energy Company in Southern California",
    paragraphs: [
      "As a local solar energy company, we help people set up systems that make sense long term. Because we understand roofing, we plan solar systems so they don't create roof issues later. That saves homeowners money and headaches down the road.",
      "Solar in Southern California has changed a lot over the years. It's not just panels anymore. Homeowners now want battery storage, backup power, and EV charging that fits their home and roof layout.",
    ],
    img: solarEnergyImg.url,
    alt: "Solar array installed on a flat commercial roof in Southern California",
    href: "/services/solar",
  },
];

function StorySections() {
  return <StorySectionsInner />;
}

function useInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      });
    }, { threshold: 0.3, ...(options ?? {}) });
    io.observe(el);
    return () => io.disconnect();
  }, [inView, options]);
  return { ref, inView };
}

function CountUp({ target, suffix = "", duration = 1600 }: { target: number; suffix?: string; duration?: number }) {
  const { ref, inView } = useInView<HTMLSpanElement>();
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return <span ref={ref}>{value.toLocaleString()}{suffix}</span>;
}

function StatsSection() {
  const stats = [
    { n: 40, suffix: "+", l: "Years in Business" },
    { n: 150, suffix: "+", l: "Google Reviews" },
    { n: 3200, suffix: "+", l: "Happy Homes" },
  ];
  return (
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-14 sm:grid-cols-3 sm:px-6 sm:py-16 lg:px-8">
      {stats.map((s, i) => (
        <div
          key={s.l}
          className={`group text-center sm:px-4 ${i > 0 ? "sm:border-l sm:border-primary-foreground/15" : ""}`}
        >
          <div className="pb-1 text-3xl font-bold leading-tight tracking-tight text-white transition-transform duration-300 group-hover:scale-105 sm:text-4xl lg:text-5xl">
            <CountUp target={s.n} suffix={s.suffix} />
          </div>
          <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-primary-foreground/80">{s.l}</div>
        </div>
      ))}
    </div>
  );
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isDesktop;
}

type ViewMode = "mobile" | "tablet" | "desktop";
function useViewMode(): ViewMode {
  const [mode, setMode] = useState<ViewMode>("mobile");
  useEffect(() => {
    const tablet = window.matchMedia("(min-width: 640px) and (max-width: 1023px)");
    const desktop = window.matchMedia("(min-width: 1024px)");
    const update = () => setMode(desktop.matches ? "desktop" : tablet.matches ? "tablet" : "mobile");
    update();
    tablet.addEventListener("change", update);
    desktop.addEventListener("change", update);
    return () => {
      tablet.removeEventListener("change", update);
      desktop.removeEventListener("change", update);
    };
  }, []);
  return mode;
}

type ServiceCard = {
  title: string;
  description: string;
  image: string;
  href: string;
  comingSoon?: boolean;
};

const roofingServices: ServiceCard[] = roofingTiles.map((t) => ({ title: t.title, description: t.description, image: t.image, href: t.href }));
const solarServices: ServiceCard[] = solarTiles.map((t) => ({ title: t.title, description: t.description, image: t.image, href: t.href }));

function ServiceTile({ item }: { item: ServiceCard }) {
  const navigate = useNavigate();
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    const overlay = document.createElement("div");
    overlay.className = "mck-explore-portal";
    overlay.innerHTML = `
      <div class="mck-explore-shell">
        <div class="mck-explore-sweep"></div>
        <div class="mck-explore-center">
          <div class="mck-explore-text">
            <div class="mck-explore-eyebrow">Loading</div>
            <div class="mck-explore-city">${item.title}</div>
            <div class="mck-explore-track"><div class="mck-explore-bar"></div></div>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = "hidden";
    window.setTimeout(() => {
      navigate({ to: item.href });
    }, 1400);
    window.setTimeout(() => {
      overlay.remove();
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }, 2400);
    // Hard safety: no matter what, unlock scroll after 4s.
    window.setTimeout(() => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.querySelectorAll(".mck-explore-portal").forEach((el) => el.remove());
    }, 4000);
  };
  return (
    <Link
      to={item.href}
      onClick={handleClick}
      className="group relative block aspect-[3/4] w-full shrink-0 overflow-hidden rounded-2xl bg-neutral-900 shadow-lg ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1"
    >
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
      {item.comingSoon && (
        <span className="absolute right-3 top-3 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur">
          Coming Soon
        </span>
      )}
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-5 text-white">
        <h3 className="font-display text-2xl font-semibold leading-tight">{item.title}</h3>
        <p className="line-clamp-2 text-sm text-white/75">{item.description}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="relative z-10 bg-accent text-accent-foreground hover:bg-accent/90"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate({ to: "/contact" });
            }}
          >
            Get Quote<span className="sr-only"> for {item.title}</span>
          </Button>
          <Button asChild variant="outline" size="sm" className="pointer-events-none border-white/40 bg-white/5 text-white hover:bg-white hover:text-primary">
            <span className="inline-flex items-center gap-1.5">
              Learn More<span className="sr-only"> about {item.title}</span> <ArrowRight className="h-3 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </Button>
        </div>
      </div>
    </Link>
  );
}

export function ServicesGrid() {
  const [tab, setTab] = useState<"roofing" | "solar">("roofing");
  const items = tab === "roofing" ? roofingServices : solarServices;
  const mode = useViewMode();
  const isCarousel = mode !== "mobile";
  const railRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const total = items.length;
  const indexRef = useRef(index);
  indexRef.current = index;

  const goTo = useCallback(
    (i: number) => setIndex(Math.min(Math.max(i, 0), total - 1)),
    [total],
  );

  // Stepping past either end of a category rolls over into the other category.
  const pendingRef = useRef<"first" | "last">("first");
  const step = useCallback(
    (dir: number) => {
      const next = indexRef.current + dir;
      if (next > total - 1) {
        pendingRef.current = "first";
        setTab((t) => (t === "roofing" ? "solar" : "roofing"));
      } else if (next < 0) {
        pendingRef.current = "last";
        setTab((t) => (t === "roofing" ? "solar" : "roofing"));
      } else {
        setIndex(next);
      }
    },
    [total],
  );

  useEffect(() => {
    setIndex(pendingRef.current === "last" ? items.length - 1 : 0);
    pendingRef.current = "first";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Wheel over the rail steps one card at a time at a relaxed pace.
  const lockRef = useRef(0);
  useEffect(() => {
    if (!isCarousel) return;
    const el = railRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const raw = horizontal ? e.deltaX : e.deltaY;
      const delta = raw * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
      if (Math.abs(delta) < 2) return;
      const dir = delta > 0 ? 1 : -1;
      e.preventDefault();
      e.stopPropagation();
      const now = performance.now();
      if (now - lockRef.current < 260) return;
      lockRef.current = now;
      // Past the last card moves to the next category, and before the first
      // card moves back into the previous category.
      step(dir);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, [isCarousel, step]);

  const touchRef = useRef<{ x: number; y: number; handled: boolean } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, handled: false };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const s = touchRef.current;
    if (!s || s.handled) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    s.handled = true;
    step(dx < 0 ? 1 : -1);
  };
  const onTouchEnd = () => {
    touchRef.current = null;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      step(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    }
  };

  // Peek carousel geometry - tablet shows 3, desktop shows 5.
  const isTablet = mode === "tablet";
  const CENTER_W = isTablet ? 250 : 340;
  const SIDE_W = isTablet ? 180 : 250;
  const GAP = isTablet ? 14 : 18;
  const MAX_SIDE = isTablet ? 1 : 2;
  const positions: Record<number, number> = {
    0: 0,
    1: CENTER_W / 2 + GAP + SIDE_W / 2,
    "-1": -(CENTER_W / 2 + GAP + SIDE_W / 2),
    2: CENTER_W / 2 + GAP + SIDE_W + GAP + SIDE_W / 2,
    "-2": -(CENTER_W / 2 + GAP + SIDE_W + GAP + SIDE_W / 2),
  } as unknown as Record<number, number>;

  const Heading = (
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl text-balance">
          Roofing And Solar Services We Offer
        </h2>
        <p className="mt-4 text-primary-foreground/85">
          Trusted Roofing and Solar services for homes and businesses across Southern California.
        </p>
      </div>
    </div>
  );

  const Toggle = (
    <div className="flex justify-center" role="tablist" aria-label="Service category">
      <div className="relative flex -skew-x-12 items-center border border-white/25 bg-white/10 p-1 shadow-elegant backdrop-blur-sm">
        <span
          aria-hidden="true"
          className="absolute left-1 top-1 h-[calc(100%-0.5rem)] w-[calc(50%-0.25rem)] bg-accent shadow-glow transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ transform: tab === "solar" ? "translateX(100%)" : "translateX(0)" }}
        />
        {(["roofing", "solar"] as const).map((key) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              className={`relative z-10 w-32 px-6 py-2.5 text-sm font-semibold uppercase tracking-[0.18em] transition-colors sm:w-36 ${
                active ? "text-accent-foreground" : "text-white/65 hover:text-white"
              }`}
            >
              <span className="block skew-x-12">{key === "roofing" ? "Roofing" : "Solar"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <section className="relative overflow-hidden bg-primary pt-14 pb-6 text-primary-foreground lg:pt-16 lg:pb-8">
      <SectionDecor variant="panel" side="left" tone="white" opacity={0.06} size={780} />
      <SectionDecor variant="sun" side="right" tone="white" opacity={0.05} size={620} />

      {Heading}
      <div className="relative mt-6">{Toggle}</div>

      {/* Mobile: simple stacked grid */}
      <div className="mx-auto max-w-sm px-4 pt-8 sm:hidden">
        <div className="flex flex-col gap-6">
          {items.map((item) => (
            <div key={item.title} className="w-full">
              <ServiceTile item={item} />
            </div>
          ))}
        </div>
      </div>

      {/* Tablet & desktop: peek carousel rail with prev/next controls */}
      <div className="relative mt-1 hidden w-full sm:block">
        <div
          ref={railRef}
          tabIndex={0}
          role="group"
          aria-roledescription="carousel"
          aria-label="Roofing and solar services"
          onKeyDown={onKeyDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className="relative mx-auto h-[430px] w-full max-w-[900px] outline-none focus-visible:ring-2 focus-visible:ring-accent/60 lg:h-[470px] lg:max-w-[1500px]"
        >
          {items.map((item, i) => {
            const signed = i - index;
            const abs = Math.abs(signed);
            const visible = abs <= MAX_SIDE;
            const isCenter = signed === 0;
            const width = isCenter ? CENTER_W : SIDE_W;
            const x = positions[signed] ?? (signed > 0 ? 900 : -900);
            const opacity = !visible ? 0 : isCenter ? 1 : abs === 1 ? 0.85 : 0.55;
            return (
              <div
                key={item.title}
                className="absolute top-1/2 left-1/2 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
                style={{
                  width: `${width}px`,
                  transform: `translate(calc(-50% + ${x}px), -50%)`,
                  opacity,
                  zIndex: 10 - abs,
                  pointerEvents: isCenter ? "auto" : "none",
                }}
                aria-hidden={!isCenter}
              >
                <ServiceTile item={item} />
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => step(-1)}
          aria-label="Previous service"
          className="absolute left-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground shadow-lg backdrop-blur transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-30 sm:left-4 md:left-6 lg:left-10 xl:left-16"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label="Next service"
          className="absolute right-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground shadow-lg backdrop-blur transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-30 sm:right-4 md:right-6 lg:right-10 xl:right-16"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Dots */}
      <div className="mt-2 hidden items-center justify-center gap-2 sm:flex">
        {items.map((item, i) => (
          <button
            key={item.title}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to ${item.title}`}
            aria-current={i === index}
            className="flex h-6 items-center px-1"
          >
            <span
              className={`h-[3px] rounded-full transition-all duration-300 ${
                i === index ? "w-7 bg-accent" : "w-2 bg-primary-foreground/40"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
}

function RecentJobPhotos() {
  const galleryImages = [
    { src: gallery1.url, alt: "Solar panel installation on a shingle roof" },
    { src: gallery2.url, alt: "Solar array installed on a coastal Southern California home" },
    { src: gallery3.url, alt: "Solar panels on a tile roof in Southern California" },
    { src: gallery4.url, alt: "Multi-section solar installation on a residential roof" },
    { src: gallery5.url, alt: "Completed solar installation on a Southern California home exterior" },
    { src: gallery6.url, alt: "Flat-roof solar array on a Southern California home" },
    { src: gallery7.url, alt: "Roof replacement in progress with fresh underlayment" },
    { src: gallery8.url, alt: "Residential solar installation on a shingle roof" },
    { src: gallery9.url, alt: "Tudor-style home mid re-roof with fresh sheathing around brick chimney" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">
            Gallery
          </h2>
          <p className="mt-4 text-muted-foreground">
            Recent roof replacements, roof repairs, solar installs, and storm fixes from around Southern California.
          </p>
        </div>
        <BentoGallery images={galleryImages} />
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-elegant">
            <Link to="/contact">Get Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground">
            <Link to="/why-choose-us">Why Homeowners Pick Us</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

export function BentoGallery({ images }: { images: { src: string; alt: string }[] }) {
  // Bento tile spans – designed for 8 images with one large feature + supporting mix.
  const tiles = [
    "sm:col-span-2 sm:row-span-2",     // 1 – large feature
    "sm:col-span-2 sm:row-span-1",     // 2 – wide
    "sm:col-span-1 sm:row-span-1",     // 3
    "sm:col-span-1 sm:row-span-2",     // 4 – tall
    "sm:col-span-1 sm:row-span-1",     // 5
    "sm:col-span-2 sm:row-span-1",     // 6 – wide
    "sm:col-span-1 sm:row-span-1",     // 7
    "sm:col-span-1 sm:row-span-1",     // 8
    "sm:col-span-2 sm:row-span-1",     // 9 – wide, fills remaining row
  ];
  const [lightbox, setLightbox] = useState<number | null>(null);
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const sourceRectRef = useRef<DOMRect | null>(null);
  const closingRef = useRef(false);

  const prefersReduced = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (lightbox === null) {
      sourceRectRef.current = null;
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Lenis hijacks wheel events, so body overflow alone doesn't lock scroll.
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [lightbox]);

  // FLIP on the REAL lightbox image (no clone): measure its final box, then
  // animate a transform from the thumbnail's box back to identity.
  useLayoutEffect(() => {
    if (lightbox === null) return;
    const img = imageRef.current;
    const backdrop = backdropRef.current;
    if (!img) return;

    backdrop?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 260,
      easing: "ease-out",
    });

    const source = sourceRectRef.current;
    const run = () => {
      const target = img.getBoundingClientRect();
      if (!source || prefersReduced() || !target.width || !target.height) {
        img.style.opacity = "1";
        img.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200 });
        return;
      }
      // Uniform scale (max) keeps the aspect ratio – non-uniform scaling was
      // what made the transition look stretched and "unfinished".
      const scale = Math.max(source.width / target.width, source.height / target.height);
      const dx = source.left + source.width / 2 - (target.left + target.width / 2);
      const dy = source.top + source.height / 2 - (target.top + target.height / 2);
      img.style.opacity = "1";
      img.style.willChange = "transform, opacity";
      const anim = img.animate(
        [
          { transform: `translate3d(${dx}px, ${dy}px, 0) scale(${scale})`, opacity: 0.55 },
          { transform: "translate3d(0, 0, 0) scale(1)", opacity: 1 },
        ],
        { duration: 460, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
      );
      anim.finished
        .then(() => {
          img.style.willChange = "auto";
        })
        .catch(() => {});
    };

    if (img.complete) run();
    else img.addEventListener("load", run, { once: true });
    return () => {
      img.removeEventListener("load", run);
      img.getAnimations().forEach((a) => a.cancel());
    };
  }, [lightbox]);

  function openLightbox(i: number) {
    const thumbImg = thumbRefs.current[i]?.querySelector("img");
    sourceRectRef.current = thumbImg ? thumbImg.getBoundingClientRect() : null;
    setLightbox(i);
  }

  function closeLightbox() {
    if (lightbox === null || closingRef.current) return;
    const img = imageRef.current;
    const backdrop = backdropRef.current;
    const thumbImg = thumbRefs.current[lightbox]?.querySelector("img");
    const source = thumbImg?.getBoundingClientRect();
    if (!img || !source || prefersReduced()) {
      setLightbox(null);
      return;
    }
    closingRef.current = true;
    img.getAnimations().forEach((a) => a.cancel());

    const target = img.getBoundingClientRect();
    const scale = Math.max(source.width / target.width, source.height / target.height);
    const dx = source.left + source.width / 2 - (target.left + target.width / 2);
    const dy = source.top + source.height / 2 - (target.top + target.height / 2);

    backdrop?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 300,
      easing: "ease-in",
      fill: "forwards",
    });

    img.style.willChange = "transform, opacity";
    const anim = img.animate(
      [
        { transform: "translate3d(0, 0, 0) scale(1)", opacity: 1 },
        { transform: `translate3d(${dx}px, ${dy}px, 0) scale(${scale})`, opacity: 0 },
      ],
      { duration: 340, easing: "cubic-bezier(0.4, 0, 1, 1)", fill: "forwards" },
    );
    const done = () => {
      closingRef.current = false;
      setLightbox(null);
    };
    anim.finished.then(done).catch(done);
  }

  return (
    <>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:auto-rows-[140px] sm:grid-cols-4 sm:gap-4 md:auto-rows-[160px] lg:auto-rows-[180px]">
        {images.map((s, i) => (
          <button
            type="button"
            key={s.src}
            ref={(el) => { thumbRefs.current[i] = el; }}
            onClick={() => openLightbox(i)}
            className={`group relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-muted shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-elegant focus:outline-none focus:ring-2 focus:ring-accent sm:aspect-auto ${tiles[i] ?? ""}`}
            aria-label={`Open ${s.alt}`}
          >
            <img
              src={s.src}
              alt={s.alt}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </button>
        ))}
      </div>
      {lightbox !== null && typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            onClick={() => closeLightbox()}
            onWheel={(e) => e.preventDefault()}
            data-lenis-prevent
            role="dialog"
            aria-modal="true"
          >
            {/* Backdrop fades on its own so it never tints the flying image */}
            <div
              ref={backdropRef}
              className="pointer-events-none absolute inset-0 bg-black/90"
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              className="absolute right-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              ref={imageRef}
              src={images[lightbox].src}
              alt={images[lightbox].alt}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 max-h-[90vh] max-w-[95vw] rounded-xl object-contain shadow-2xl"
            />
          </div>,
          document.body,
        )}
    </>
  );
}

const faqs = [
  {
    q: "Do you offer free estimates?",
    a: "Yes. We provide free, no-obligation estimates for roofing, solar panels, and battery systems throughout Carlsbad and Southern California.",
  },
  {
    q: "How do I know if my roof needs repair or full replacement?",
    a: "Most roofs give warning signs before they fail. Stains on ceilings, missing or cracked shingles, and leaks during rain usually point to repair. If the roof is older, has widespread wear, or keeps leaking in new spots, replacement may make more sense. We'll look at the roof and explain what we see so you can decide.",
  },
  {
    q: "What roofing material works best for Southern California homes?",
    a: "In Southern California, asphalt shingles, tile, and metal roofing all perform well when installed right. The best choice depends on sun exposure, roof slope, and budget. Tile roofs last a long time in heat. Shingles cost less upfront. Metal reflects heat well. We walk through options so it fits your home, not just the trend.",
  },
  {
    q: "Is solar actually worth it in Southern California?",
    a: "For most homes, yes! Southern California gets consistent sun year round which makes solar a strong long-term investment. Homeowners see lower electric bills right away when they work with us and savings add up over time. How much you save depends on usage, roof layout, and system size.",
  },
  {
    q: "How much does solar cost in Southern California?",
    a: "Solar costs in Southern California vary based on system size, roof layout, and energy use. Many homeowners are surprised that solar can be installed with $0 out of pocket through financing options.\n\nMonthly payments are often lower than utility bills, so people start saving right away. Incentives and tax credits also help lower the overall cost. We go over all the numbers before anything moves forward.",
  },
];

function CommonQuestions() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="relative overflow-hidden bg-muted/30">
      <SectionDecor variant="roof" side="right" tone="primary" opacity={0.05} size={700} />
      <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">
            Common Questions
          </h2>
          <p className="mt-4 text-muted-foreground">
            Text or call{" "}
            <a href={company.phoneHref} className="font-semibold text-secondary hover:underline">
              {company.phone}
            </a>{" "}
            we're here to help!
          </p>
        </div>
        <div className="mt-10 space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left sm:px-6 sm:py-5"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm font-semibold text-primary sm:text-base">{f.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-primary transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="whitespace-pre-line px-5 pb-5 text-sm leading-relaxed text-foreground/85 sm:px-6 sm:pb-6 sm:text-base">
                      {f.a}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StorySectionsInner() {
  const isDesktop = useIsDesktop();
  if (isDesktop) return <StorySectionsDesktop />;
  return (
    <section className="bg-background" aria-label="Our services">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="flex flex-col gap-16 sm:gap-24">
          {stories.map((s, i) => (
            <article
              key={s.title}
              className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-14"
            >
              <div
                className={`relative h-[240px] w-full overflow-hidden rounded-3xl border border-border shadow-elegant sm:h-[320px] lg:h-[420px] ${
                  i % 2 === 1 ? "lg:order-2" : ""
                }`}
              >
                <img
                  src={s.img}
                  alt={s.alt}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-transparent" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent sm:text-xs lg:text-sm">
                  {s.eyebrow}
                </div>
                <h2 className="mt-2 text-xl font-bold text-primary sm:text-2xl lg:text-4xl text-balance">
                  {s.title}
                </h2>
                <div className="mt-4 space-y-3 sm:space-y-4">
                  {s.paragraphs.map((p, idx) => (
                    <p key={idx} className="text-xs leading-relaxed text-foreground/85 sm:text-sm lg:text-base">
                      {p}
                    </p>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button asChild className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground">
                    <Link to={s.href}>Learn More<span className="sr-only"> about {s.title}</span></Link>
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function StorySectionsDesktop() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = el.offsetHeight - vh;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? scrolled / total : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const n = stories.length;
  const active = Math.min(n - 1, Math.max(0, Math.floor(progress * n)));

  return (
    <section
      ref={wrapRef}
      className="relative bg-background"
      style={{ height: `${n * 100}vh` }}
      aria-label="Our services"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <div className="mx-auto grid h-full max-w-7xl grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start gap-10 px-4 pt-32 sm:px-6 md:pt-40 lg:items-center lg:gap-14 lg:px-8 lg:pt-0">
          <div className="relative order-1 h-[60vh] w-full [perspective:1400px] lg:h-[70vh]">
            {stories.map((s, i) => {
              const offset = i - active;
              const isPast = offset < 0;
              const isTop = offset === 0;
              // Stack tuning: each card behind is slightly smaller, rotated, and offset.
              const behindIdx = Math.max(offset, 0);
              const rotates = [0, -4, 5, -3, 4, -5];
              const rotate = isTop ? 0 : rotates[behindIdx % rotates.length];
              const translateY = isTop ? 0 : behindIdx * 14;
              const translateX = isTop ? 0 : (behindIdx % 2 === 0 ? -1 : 1) * behindIdx * 8;
              const scale = isTop ? 1 : Math.max(0.9, 1 - behindIdx * 0.04);
              const z = stories.length - Math.abs(offset);
              // Past cards slide fully off-screen to the side – no fade.
              const pastTransform = "translate(-180%, 25%) rotate(-22deg) scale(0.92)";
              const stackTransform = `translate(${translateX}px, ${translateY}px) rotate(${rotate}deg) scale(${scale})`;
              return (
                <div
                  key={s.img}
                  className="absolute inset-0 origin-center overflow-hidden rounded-3xl border border-border bg-background shadow-elegant transition-all duration-700 ease-out will-change-transform"
                  style={{
                    transform: isPast ? pastTransform : stackTransform,
                    opacity: offset > 4 ? 0 : 1,
                    zIndex: z + 10,
                    pointerEvents: isTop ? "auto" : "none",
                  }}
                  aria-hidden={!isTop}
                >
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-primary/25 via-transparent to-transparent" />
                </div>
              );
            })}
          </div>

          <div className="order-2 flex h-full items-start lg:items-center">
            <div className="relative min-h-[420px] w-full">
              {stories.map((s, i) => (
                <div
                  key={s.title}
                  className={`absolute inset-0 transition-all duration-700 ease-out ${
                    i === active
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : i < active
                        ? "opacity-0 -translate-y-6 pointer-events-none"
                        : "opacity-0 translate-y-6 pointer-events-none"
                  }`}
                  aria-hidden={i !== active}
                >
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent lg:text-sm">
                    {s.eyebrow}
                  </div>
                  <h2 className="mt-3 text-2xl font-extrabold text-primary md:text-3xl lg:text-5xl text-balance">
                    {s.title}
                  </h2>
                  <div className="mt-5 space-y-4">
                    {s.paragraphs.map((p, idx) => (
                      <p key={idx} className="text-sm leading-relaxed text-foreground/85 md:text-base lg:text-lg">
                        {p}
                      </p>
                    ))}
                  </div>
                  <div className="mt-7">
                    <Button asChild className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                      <Link to="/contact">
                        Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="ml-3 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground">
                      <Link to={s.href}>Learn More<span className="sr-only"> about {s.title}</span></Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
